import React, { useEffect, useMemo, useState } from "react";

import { TextField } from "ui/components/TextField/TextField";
import { DatePicker } from "ui/components/DatePicker";
import { CheckBox } from "ui/components/CheckBox";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

import {
  useHandleTextFieldChange,
  useHandleCheckboxChange,
  useHandleDateChange,
  useHandleDropdownChange,
} from "services/forms/hooks";
import { EkoProgram } from "services/api/catalog/cvh/types";
import { getEkoProgramAPI } from "services/api/catalog/cvh/api";
import { useTranslate } from "helpers";

import { UsedPriceVehicleDetailsFormProps } from "./types";

/** Drugi korak: podaci koji ulaze u izračun, odvojeni od odabira vozila. */
const UsedPriceVehicleDetailsForm: React.FC<UsedPriceVehicleDetailsFormProps> = ({
  show,
  formValues,
  setFormValues,
  errors,
}) => {
  const { t } = useTranslate();
  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleCheckboxChange = useHandleCheckboxChange(setFormValues);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);

  const [ekoProgram, setEkoProgram] = useState<EkoProgram[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!show) return;

    fetchEkoProgram();
  }, [show]);

  const fetchEkoProgram = async () => {
    setIsLoading(true);
    try {
      const data = await getEkoProgramAPI();
      setEkoProgram(data);
    } catch {
      setEkoProgram([]);
    }
    setIsLoading(false);
  };

  const ekoProgramOptions: DropdownOption[] = useMemo(() => {
    return ekoProgram.map((e) => ({
      value: e.id,
      text: e.name,
    }));
  }, [ekoProgram]);

  return (
    <div className="px-4 space-y-4">
      <div className="border-slate-200 border px-3 pt-4 pb-2">
        <p className="text-sm text-slate-700 mb-2 font-bold">Detalji vozila</p>
        <p className="text-sm text-slate-500 mb-3">
          Popunite detalje vozila kako bi dobili što točniji izračun
        </p>
        <div className="grid grid-cols-4 gap-3">
          <div className="mb-3">
            <DatePicker
              name="priceOnDate"
              label={t("admin.fleet.vehicle.residualvalue.priceondate")}
              placeholder={t("admin.fleet.vehicle.residualvalue.priceondate.placeholder")}
              value={formValues.priceOnDate}
              onChange={handleDateChange}
            />
          </div>
          <div className="mb-3">
            <DatePicker
              name="firstRegistrationDate"
              label={t("admin.fleet.vehicle.residualvalue.firstregistrationdate")}
              placeholder={t("admin.fleet.vehicle.residualvalue.firstregistrationdate.placeholder")}
              value={formValues.firstRegistrationDate}
              onChange={handleDateChange}
              error={errors.firstRegistrationDate}
              required
            />
          </div>
          <div className="mb-3">
            <TextField
              type="number"
              name="mileageKm"
              label={t("admin.fleet.vehicle.residualvalue.mileagekm")}
              placeholder={t("admin.fleet.vehicle.residualvalue.mileagekm.placeholder")}
              value={formValues.mileageKm ?? ""}
              onChange={handleTextFieldChange}
              groupAppend="km"
              error={errors.mileageKm}
              required
            />
          </div>
          <div className="mb-3">
            <TextField
              type="number"
              name="co2"
              label={t("admin.fleet.vehicle.residualvalue.co2")}
              placeholder={t("admin.fleet.vehicle.residualvalue.co2.placeholder")}
              value={formValues.co2 ?? ""}
              onChange={handleTextFieldChange}
              groupAppend="g/km"
            />
          </div>
          <div className="mb-3">
            <TextField
              type="number"
              name="numberOfSeats"
              label={t("admin.fleet.vehicle.residualvalue.numberofseats")}
              placeholder={t("admin.fleet.vehicle.residualvalue.numberofseats.placeholder")}
              value={formValues.numberOfSeats ?? ""}
              onChange={handleTextFieldChange}
            />
          </div>
          <div className="mb-3">
            <TextField
              type="number"
              name="electricRange"
              label={t("admin.fleet.vehicle.residualvalue.electricrange")}
              placeholder={t("admin.fleet.vehicle.residualvalue.electricrange.placeholder")}
              value={formValues.electricRange ?? ""}
              onChange={handleTextFieldChange}
              groupAppend="km"
            />
          </div>
          <div className="mb-3">
            <TextField
              type="number"
              name="enginePower"
              label={t("admin.fleet.vehicle.residualvalue.enginepower")}
              placeholder={t("admin.fleet.vehicle.residualvalue.enginepower.placeholder")}
              value={formValues.enginePower ?? ""}
              onChange={handleTextFieldChange}
              groupAppend="kw"
            />
          </div>
          <div className="mb-3">
            <TextField
              type="number"
              name="engineDisplacement"
              label={t("admin.fleet.vehicle.residualvalue.enginedisplacement")}
              placeholder={t("admin.fleet.vehicle.residualvalue.enginedisplacement.placeholder")}
              value={formValues.engineDisplacement ?? ""}
              onChange={handleTextFieldChange}
              groupAppend="ccm"
            />
          </div>
          <div className="mb-3">
            <Dropdown
              name="ecoProgramId"
              label={t("admin.fleet.vehicle.residualvalue.ecoprogramid")}
              placeholder={t("admin.fleet.vehicle.residualvalue.ecoprogramid.placeholder")}
              value={formValues.ecoProgramId}
              options={ekoProgramOptions}
              onChange={handleDropdownChange}
              isLoading={isLoading}
            />
          </div>
        </div>

        <div className="flex gap-6 pt-2">
          <CheckBox
            name="ppmvPassengerVehicles"
            checked={formValues.ppmvPassengerVehicles}
            onChange={handleCheckboxChange}
            label={t("admin.fleet.vehicle.residualvalue.ppmvpassengervehicles")}
          />
        </div>
      </div>
    </div>
  );
};

export default UsedPriceVehicleDetailsForm;
